import { defineStore } from "pinia";
import { wpApi, customApi, partnerApi } from "@app/helpers";
import {
  parseQueryString,
  getConsultationPriceBreakdown,
  calculateTotalPrice,
  calculateFinalPrice,
  calculatePatientFees,
  calculateClinicFees,
} from "@app/helpers"; // Import the helper
import { useRoute } from "vue-router";
import { cloneDeep } from "lodash-es";

const route = useRoute();

const BOOKING_STORAGE_KEY = "travelJabBookingData";
const CLINIC_STORAGE_KEY = "travelJabSelectedClinic"; // New key for clinic

export const useBookingStore = defineStore("bookingData", {
  state: () => ({
    serviceType: "vaccinations",
    vaccinations: [], // stores all vaccinations available.
    destinations: [], // stores all destinations available.
    clinics: [], // stores all clinics found that matches the user query.
    clinic: null, // stores the selected clinic by the user, initialize as null
    clinicVaccinationsReady: false, // indicates if the clinic vaccinations are loaded
    query: {
      vaccinations: [], // stores all vaccinations selected by the user.
      destination: null, // stores the destination object selected by the user for consultations (e.g., {id, name, url}).
      dates: null, // stores the travel dates for consultations (array with [departure, return])
      coursePreferences: {}, // tracks vaccination course preferences (vaccinationId: 'full' | 'single')
      patients: {
        total: 0,
        adult65: 0,
        adult16: 0,
        youngAdult: 0,
        children: 0,
        infant: 0,
      },
    },
    currentDestinationInfo: null, // Holds detailed info for the selected destination
    booking: {
      // Initial empty/default booking state
      date: null, // stores the selected date for the booking.
      time: null, // stores the selected time for the booking.
      patients: [], // stores the patients selected for the booking.
      clinic: {}, // stores the selected clinic details for the booking.
    },
    forms: [], // used to store form validation states for each patient
  }),

  actions: {
    // Action to load booking data from localStorage
    loadBookingFromLocalStorage() {
      const storedBooking = localStorage.getItem(BOOKING_STORAGE_KEY);
      if (storedBooking) {
        try {
          const parsedBooking = JSON.parse(storedBooking);
          // Merge with existing booking state to preserve defaults or structure
          // Be careful with deep merging if structures are complex or can be partial.
          // A simple overwrite is often fine if the stored object is always complete.
          this.booking = { ...this.booking, ...parsedBooking };
          console.log(
            "BookingStore: Booking data loaded from localStorage.",
            this.booking
          );
        } catch (error) {
          console.error(
            "BookingStore: Error parsing booking data from localStorage.",
            error
          );
          localStorage.removeItem(BOOKING_STORAGE_KEY); // Clear corrupted data
        }
      }
    },

    async loadQueryFromURL() {
      if (Object.keys(this.query?.patients?.total).length === 0) {
        const url = parseQueryString(window.location.href);
        if (url?.serviceType) {
          this.query = parseQueryString(window.location.href);
        }
      }

      // if user directly navigates to a URL with a clinic_id, load the clinic data
      if (this.query?.clinic_id) {
        const clinicId = this.query.clinic_id;
        const serviceType = this.query.serviceType || this.serviceType;

        try {
          const response = await this.getClinic(clinicId);

          if (response.status !== 200) {
            throw new Error(
              `Failed to load clinic with ID: ${clinicId}. Status: ${response.status}`
            );
          }

          console.log("clinic loaded from URL", response.data);
          this.setClinic(response.data[0]); // Assuming the first clinic is the one we want

          this.clinic.basket = {
            vaccinations: {
              adult: [],
              child: [],
            },
            consultations: [],
          };

          await Promise.all([
            this.vaccinations.length === 0 && this.getVaccinations(),
            this.destinations.length === 0 && this.getDestinations(),
          ]);

          if (serviceType === "vaccinations") {
            const clinicVaccinationResponse = await this.getClinicVaccinations(
              clinicId
            );

            if (clinicVaccinationResponse.status !== 200) {
              throw new Error(
                `Failed to load clinic vaccinations. Status: ${clinicVaccinationResponse.status}`
              );
            }

            const clinicVaccinations = clinicVaccinationResponse.data; // Assuming response.data is the array of vaccinations
            this.loadClinicVaccinations(this.clinic, clinicVaccinations);

            this.clinic.totalPrice = calculateTotalPrice(this.clinic);
            this.clinic.totalFees = calculateClinicFees(this.clinic.totalPrice);
            this.clinic.finalPrice = calculateFinalPrice(this.clinic);
            this.clinicVaccinationsReady = true;

            console.log(
              "BookingStore: Clinic vaccinations loaded successfully.",
              this.clinicVaccinationsReady
            );
          } else {
            const clinicConsultationResponse =
              await this.getClinicConsultationPrices(clinicId);

            if (clinicConsultationResponse.status !== 200) {
              throw new Error(
                `Failed to load clinic consultations. Status: ${clinicConsultationResponse.status}`
              );
            }

            const clinicConsultations = clinicConsultationResponse.data;

            const consultationDetails = getConsultationPriceBreakdown(
              clinicConsultations,
              this.query.patients
            );

            this.clinic.totalPrice = consultationDetails.totalPrice;
            this.clinic.basket.consultations = consultationDetails;
          }
        } catch (error) {
          console.error(`BookingStore: ${error.message}`);
        }
      }
    },

    loadClinicVaccinations(clinic = this.clinic, clinicVaccinations) {
      const updatedVaccinations = clinicVaccinations.map((vacc) => {
        const matchingBooking = this.vaccinations.find(
          (vaccination) => vaccination.id == vacc.vac_id
        );
        const name = matchingBooking ? matchingBooking.name : null;
        return {
          id: vacc.vac_id,
          priceBeforeFees: Number(vacc.price),
          price: Number(vacc.price) + calculatePatientFees(vacc.price),
          name,
          dose: matchingBooking ? matchingBooking.dose : null,
          course: "single",
          forChildren: name && name.toLowerCase().includes("children"), // Check if name contains 'children'
        };
      });

      // Computed property to filter for adult vaccinations
      const adultVaccinations = updatedVaccinations.filter(
        (vacc) => !vacc.forChildren
      );

      // Computed property to filter for children vaccinations
      const childVaccinations = updatedVaccinations.filter(
        (vacc) => vacc.forChildren
      );

      for (let i = 0; i < this.query.patients.totalAdults; i++) {
        // Check if adultVaccinations is not empty before pushing
        if (adultVaccinations && adultVaccinations.length > 0) {
          clinic.basket.vaccinations.adult.push(cloneDeep(adultVaccinations));
        }
      }

      for (let i = 0; i < this.query.patients.totalChildren; i++) {
        // Check if childVaccinations is not empty before pushing
        if (childVaccinations && childVaccinations.length > 0) {
          clinic.basket.vaccinations.child.push(cloneDeep(childVaccinations));
        }
      }
    },

    // Action to save booking data to localStorage
    saveBookingToLocalStorage() {
      try {
        localStorage.setItem(BOOKING_STORAGE_KEY, JSON.stringify(this.booking));
        // console.log("BookingStore: Booking data saved to localStorage.", this.booking);
      } catch (error) {
        console.error(
          "BookingStore: Error saving booking data to localStorage.",
          error
        );
      }
    },

    // Action to clear only the booking-specific details from store and localStorage
    // Keeps clinic, query, etc.
    clearBookingDetails() {
      this.booking = {
        // Reset to initial empty/default booking state
        date: null,
        time: null,
        patients: [],
        success: {
          bookingId: null,
        },
        // Reset other booking-specific properties if you have them
        // id: null, bookingRef: null, totalPrice: 0, serviceType: null
      };
      localStorage.removeItem(BOOKING_STORAGE_KEY);
      console.log(
        "BookingStore: Booking details cleared from store and localStorage."
      );
    },

    // Action to clear all booking related data including clinic and query (for a full reset)
    clearAllBookingFlowData() {
      this.clearBookingDetails(); // Clears booking and its localStorage
      this.resetClinic(); // Clears clinic and its localStorage
      this.query = {
        // Reset query object
        vaccinations: [],
        destination: null,
        coursePreferences: {},
        patients: {
          total: 0,
          adult65: 0,
          adult16: 0,
          youngAdult: 0,
          child: 0,
          infant: 0,
        },
      };
      this.forms = []; // Reset forms
      console.log(
        "BookingStore: Booking data cleared from store and localStorage."
      );
    },

    // Action to load clinic data from localStorage
    loadClinicFromLocalStorage() {
      const storedClinic = localStorage.getItem(CLINIC_STORAGE_KEY);
      if (storedClinic) {
        try {
          this.clinic = JSON.parse(storedClinic);
          console.log(
            "BookingStore: Clinic data loaded from localStorage.",
            this.clinic
          );
        } catch (error) {
          console.error(
            "BookingStore: Error parsing clinic data from localStorage.",
            error
          );
          localStorage.removeItem(CLINIC_STORAGE_KEY); // Clear corrupted data
        }
      }
    },

    // Action to save clinic data to localStorage
    saveClinicToLocalStorage() {
      if (this.clinic) {
        localStorage.setItem(CLINIC_STORAGE_KEY, JSON.stringify(this.clinic));
        // console.log("BookingStore: Clinic data saved to localStorage.", this.clinic);
      } else {
        // If clinic is null/undefined, remove it from localStorage
        localStorage.removeItem(CLINIC_STORAGE_KEY);
      }
    },

    setServiceType(type) {
      this.serviceType = type;
    },

    // Action to initialize or synchronize the forms array based on the number of patients
    synchronizeFormsArray(numPatients) {
      if (typeof numPatients !== "number" || numPatients < 0) {
        console.error(
          "Invalid number of patients for synchronizing forms array."
        );
        return;
      }

      const currentFormsLength = this.forms.length;
      if (currentFormsLength === numPatients) {
        return; // No change needed
      }

      const newForms = Array(numPatients)
        .fill(null)
        .map((_, i) => {
          // Preserve existing form meta if available, otherwise initialize
          return (
            this.forms[i] || {
              valid: false,
              dirty: false,
              touched: false,
              pending: false,
              initialValues: {},
            }
          );
        });
      this.forms = newForms;
      // console.log(`Forms array synchronized to ${numPatients} entries.`);
    },

    setResults(clinics) {
      this.clinics = clinics;
    },

    setClinic(clinic) {
      this.clinic = clinic;
      // Optionally, automatically save to localStorage whenever the clinic is set.
      // If you prefer explicit saving, remove the line below and call saveClinicToLocalStorage() from components.
      this.saveClinicToLocalStorage();
    },

    resetClinic() {
      this.clinic = null;
      this.saveClinicToLocalStorage(); // This will remove it from localStorage
    },

    resetClinics() {
      this.clinics = [];
    },

    getClinicById(id) {
      if (!this.clinics || !Array.isArray(this.clinics)) {
        console.warn(
          "BookingStore: 'clinics' state is not an array or is not initialized."
        );
        return undefined;
      }
      return this.clinics.find((clinic) => clinic.id === id);
    },

    async getVaccinations() {
      try {
        const response = await customApi.get("/vaccinations");

        this.vaccinations =
          response.status === 200
            ? response.data?.map((item) => {
                return {
                  id: item.id,
                  name: item.name,
                  url: item.url,
                  category: item.category,
                  dose: item.dose,
                  fullcourse: false,
                };
              })
            : [];
      } catch (e) {
        console.error(e);
      }
    },

    async getDestinations() {
      try {
        const response = await customApi.get("/destinations");
        this.destinations =
          response.status === 200
            ? response.data?.map((item) => {
                return {
                  id: item.id,
                  name: item.name,
                  url: item.url,
                };
              })
            : [];
      } catch (e) {
        console.error(e);
      }
    },

    async getPlaces(postcode) {
      try {
        const response = await customApi.get(`/places?query=${postcode}`);
        if (response.status === 200) {
          return response.data;
        }
      } catch (error) {
        console.error("Error fetching places:", error.message || error);

        // Axios specific error object
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
          console.error("Response headers:", error.response.headers);
        } else if (error.request) {
          console.error("No response received, request info:", error.request);
        }
      }
    },

    async getPlacesAutocomplete(inputValue, sessionToken = null) {
      try {
        const params = { input: inputValue };
        if (sessionToken) {
          params.sessiontoken = sessionToken;
        }
        const response = await customApi.get(`/places/autocomplete`, {
          params,
        });
        if (response.status === 200) {
          return response.data;
        }
        // It's good practice to return a consistent value or throw an error if not 200
        // For example, return null or an empty array, or throw new Error(...)
        return null;
      } catch (error) {
        console.error("Error fetching places:", error.message || error);

        // Axios specific error object
        if (error.response) {
          console.error("Response data:", error.response.data);
          console.error("Response status:", error.response.status);
          console.error("Response headers:", error.response.headers);
        } else if (error.request) {
          console.error("No response received, request info:", error.request);
        }
        // Re-throw the error or return a specific error object if you want to handle it further up the call stack
        throw error;
      }
    },

    async getPostcodeFromAddress(addressString) {
      if (
        !addressString ||
        typeof addressString !== "string" ||
        addressString.trim() === ""
      ) {
        console.error(
          "Address string is required and must be a non-empty string."
        );
        // Optionally, return a specific error or null, or throw an error
        // For consistency with other methods, let's throw an error or return null.
        return null;
      }
      try {
        const response = await customApi.get(`/geocode/address`, {
          params: {
            address: addressString,
          },
        });
        // The backend should return { postcode: "YOUR_POSTCODE" } on success
        if (
          response.status === 200 &&
          response.data &&
          response.data.postcode
        ) {
          return response.data.postcode;
        }
        // If backend returns 200 but no postcode (e.g. postcode_not_found error was handled and returned as 200 with a message)
        // This depends on how you structure non-WP_Error errors from PHP if not returning a WP_Error object.
        // Given the PHP code returns WP_Error for not found, this path might not be hit for "not found".
        console.warn(
          "Postcode not found or invalid response structure:",
          response.data
        );
        return null;
      } catch (error) {
        console.error(
          "Error fetching postcode from address:",
          error.message || error
        );
        // Axios error object structure is helpful for debugging
        if (error.response) {
          console.error(
            "Response data (from backend error):",
            error.response.data
          ); // WP_Error details
          console.error("Response status:", error.response.status);
        }
        throw error; // Re-throw to allow calling component to handle it
      }
    },

    async getClinic(clinicId) {
      try {
        const response = await partnerApi.get(`/clinic/profile/${clinicId}`);
        return { status: response.status, data: response.data };
      } catch (e) {
        if (e.response) {
          // Server responded with a status other than 2xx
          return {
            status: e.response.status,
            message:
              e.response.status === 404
                ? "No clinic found."
                : e.response.statusText,
          };
        } else if (e.request) {
          // Request was made but no response received
          return {
            status: 0,
            message: "No response received from server.",
          };
        } else {
          // Something went wrong in setting up the request
          return {
            status: 0,
            message: `Request setup error: ${e.message}`,
          };
        }
      }
    },

    async getClinics(location, vaccinations = []) {
      try {
        // Construct query string using URLSearchParams
        const queryParams = new URLSearchParams({
          location: location,
        });

        if (this.serviceType === "vaccinations" && vaccinations.length > 0) {
          // Add vaccination IDs to the queryParams
          vaccinations.forEach((vac) => {
            if (
              typeof vac === "object" &&
              vac !== null &&
              vac.id !== undefined
            ) {
              queryParams.append("vaccinations[]", vac.id);
            }
          });
        }

        // Convert queryParams to string
        const queryString = queryParams.toString();

        // Construct the full URL with parameters
        const url = `/clinics/nearby?${queryString}`;

        // Fetch data from the API
        const response = await partnerApi.get(url);
        return { status: response.status, data: response.data };
      } catch (e) {
        if (e.response) {
          // Server responded with a status other than 2xx
          return {
            status: e.response.status,
            message:
              e.response.status === 404
                ? "No clinics were found."
                : e.response.statusText,
          };
        } else if (e.request) {
          // Request was made but no response received
          return {
            status: 0,
            message: "No response received from server.",
          };
        } else {
          // Something went wrong in setting up the request
          return {
            status: 0,
            message: `Request setup error: ${e.message}`,
          };
        }
      }
    },

    async getClinicVaccinations(clinicId) {
      try {
        const response = await partnerApi.get(
          `/clinics/${clinicId}/vaccinations`,
          {
            params: {
              vaccinations: this.query.vaccinations.map((vac) => vac.id),
            },
          }
        );
        return response.status === 200 ? response.data : null;
      } catch (e) {
        console.error(e);
      }
    },

    async getClinicOpeningTimes(clinicId) {
      try {
        const response = await partnerApi.get(
          `/clinics/${clinicId}/opening-times`
        );
        return response.status === 200 ? response.data : null;
      } catch (e) {
        console.error(e);
      }
    },

    async getClinicConsultationPrices(clinicId) {
      try {
        const response = await partnerApi.get(
          `/clinics/${clinicId}/consultations`
        );
        return response.status === 200 ? response.data : null;
      } catch (e) {
        console.error(e);
      }
    },

    async fetchDestinationInfo(destinationId) {
      if (!destinationId) {
        this.currentDestinationInfo = null;
        return;
      }
      try {
        const response = await customApi.get(
          `/destination-info/${destinationId}`
        );
        this.currentDestinationInfo = response.data;
      } catch (error) {
        console.error(
          `Failed to fetch destination info for ID ${destinationId}:`,
          error
        );
        this.currentDestinationInfo = null; // Clear on error to avoid stale data
      }
    },

    // Simple methods to manage course preferences
    setCoursePreference(vaccinationId, course) {
      if (!vaccinationId) return;
      this.query.coursePreferences[vaccinationId] = course;
    },

    removeCoursePreference(vaccinationId) {
      if (!vaccinationId) return;
      delete this.query.coursePreferences[vaccinationId];
    },
  },
});
